import { useState } from 'react';
import Select from 'react-select-2';
import type { ReactElement } from 'react';

import { OrganizationSsoMethod } from '../../../../../../../typings/enums';
import useToggle from '../../../../../../hooks/useToggle';
import { selectOptionFactory } from '../../../../../../utils';
import { BUTTON_TYPE, TooltipButton } from '../../../../../generic';
import Text from '../../../../../generic/Text';
import useAuthenticationSettingsContext from '../../hooks/useAuthenticationSettingsContext';
import { useClaimDomainModal } from '../hooks';
import PendingDomains from '../PendingDomains';
import RemoveDomainConfirmationModal from '../RemoveDomainConfirmationModal';
import type { SelectOption } from '../../../../../../types';

import ClaimDomainModal from './ClaimDomainModal';

const ClaimDomain = () => {
  const { domains, settings, onDeleteDomain, location, router } = useAuthenticationSettingsContext();
  const canClaimDomain = !!settings.saml?.entryPoint && !!settings.saml?.cert;
  const [isRemoveDomainModalOpen, toggleIsRemoveDomainModalOpen] = useToggle(false);
  const [domainToRemove, setDomainToRemove] = useState<string | null>(null);
  const { toggleIsClaimDomainModalOpen, isClaimDomainModalOpen, isModalTriggeredExternally } = useClaimDomainModal();

  const relevantDomains = domains.filter(({ ssoMethod }) => ssoMethod === OrganizationSsoMethod.SAML);

  const onChangeDomains = (newValues: SelectOption[]) => {
    const removedDomain = relevantDomains.find(({ domain }) => !newValues.some(({ value }) => value === domain));
    if (removedDomain) {
      setDomainToRemove(removedDomain.domain);
      toggleIsRemoveDomainModalOpen();
    }
  };

  const claimNewDomainButton = (
    <TooltipButton
      id="claim-new-domain"
      type={BUTTON_TYPE.SECONDARY}
      isDisabled={!canClaimDomain}
      onClick={toggleIsClaimDomainModalOpen}
      showTooltip={!canClaimDomain}
      tooltip="Please save your changes to claim a domain."
    >
      Claim New Domain
    </TooltipButton>
  );

  const onConfirmRemoveDomain = async () => {
    if (domainToRemove) {
      await onDeleteDomain(domainToRemove);
    }
  };

  let body: ReactElement;
  if (relevantDomains.length > 0) {
    body = (
      <>
        <Select
          components={{ DropdownIndicator: null }}
          value={relevantDomains.map(({ domain }) => selectOptionFactory(domain))}
          inputValue=""
          onChange={(value) => onChangeDomains(value as SelectOption[])}
          menuIsOpen={false}
          isClearable={false}
          isSearchable={false}
          isMulti
        />
        <div className="margin-top-sm">{claimNewDomainButton}</div>
      </>
    );
  } else {
    body = claimNewDomainButton;
  }

  return (
    <>
      <Text.H3>Redirect Email Domains</Text.H3>
      <div className="gray-rounded-box margin-top-sm">
        <Text.Paragraph>
          To prevent members of your company from creating duplicate organizations, FOSSA will redirect users with your
          claimed email domains to your organization’s sign in page where they can then use SSO to sign in. All users
          with a matching email domain will be required to sign in using SAML.
        </Text.Paragraph>
        {body}
        <PendingDomains method={OrganizationSsoMethod.SAML} />
      </div>
      <ClaimDomainModal
        isOpen={isClaimDomainModalOpen}
        onHide={() => {
          toggleIsClaimDomainModalOpen();
          if (isModalTriggeredExternally) {
            // Clear out the query parameters.
            router.push(location.pathname);
          }
        }}
      />
      <RemoveDomainConfirmationModal
        isOpen={isRemoveDomainModalOpen}
        domainToRemove={domainToRemove}
        onHide={() => {
          toggleIsRemoveDomainModalOpen();
          setDomainToRemove(null);
        }}
        onConfirm={onConfirmRemoveDomain}
      />
    </>
  );
};

export default ClaimDomain;
